import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Input,
    Button,
    Space,
    Tag,
    Switch,
    Tooltip,
    Card,
    Typography,
    Row,
    Col,
    Spin,
    Modal,
    Descriptions
} from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import {
    useAllLoanEligibilityConfigs,
    useToggleLoanEligibilityConfigActive,
    useDeleteLoanEligibilityConfig,
    useEvictLoanEligibilityConfigCache
} from '../hooks/useLoanEligibilityConfig';
import { LoanEligibilityConfigDTO } from '@/shared/api/model';
import {useTheme} from "@/components/common/ThemeProvider";

const { Title, Text } = Typography;
const { confirm } = Modal;

const LoanEligibilityConfigListView: React.FC = () => {
    const navigate = useNavigate();
    const { dark } = useTheme();
    const [searchText, setSearchText] = useState('');

    const { data: configs, isLoading, refetch } = useAllLoanEligibilityConfigs();
    const toggleActive = useToggleLoanEligibilityConfigActive();
    const deleteConfig = useDeleteLoanEligibilityConfig();
    const evictCache = useEvictLoanEligibilityConfigCache();

    // Handle status toggle
    const handleStatusToggle = (id: number) => {
        toggleActive.mutate(id);
    };

    // Handle delete
    const handleDelete = (id: number, configName: string) => {
        confirm({
            title: 'Delete Configuration',
            content: `Are you sure you want to delete "${configName}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => {
                deleteConfig.mutate(id);
            }
        });
    };

    // Handle cache eviction
    const handleEvictCache = () => {
        confirm({
            title: 'Evict Cache',
            content: 'Are you sure you want to clear all loan eligibility configuration caches?',
            okText: 'Evict',
            cancelText: 'Cancel',
            onOk: () => {
                evictCache.mutate();
            }
        });
    };

    // Filter data based on search
    const filteredData = React.useMemo(() => {
        if (!configs) return [];

        return configs.filter(
            (item) =>
                item.configName?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.branch?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.glNo?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.notes?.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [configs, searchText]);

    // Table columns
    const columns = [
        {
            title: 'Configuration Name',
            dataIndex: 'configName',
            key: 'configName',
            sorter: (a: LoanEligibilityConfigDTO, b: LoanEligibilityConfigDTO) =>
                (a.configName || '').localeCompare(b.configName || ''),
            render: (text: string, record: LoanEligibilityConfigDTO) => (
                <Space>
                    <Text strong>{text}</Text>
                    {record.priority && record.priority > 0 && (
                        <Tag color="blue">Priority: {record.priority}</Tag>
                    )}
                </Space>
            )
        },
        {
            title: 'Scope',
            key: 'scope',
            render: (_: any, record: LoanEligibilityConfigDTO) => {
                if (record.branch && record.glNo) {
                    return (
                        <Space direction="vertical" size="small">
                            <Tag color="green">Branch: {record.branch}</Tag>
                            <Tag color="blue">Product: {record.glNo}</Tag>
                        </Space>
                    );
                } else if (record.branch) {
                    return <Tag color="green">Branch: {record.branch}</Tag>;
                } else if (record.glNo) {
                    return <Tag color="blue">Product: {record.glNo}</Tag>;
                } else {
                    return <Tag color="purple">Global</Tag>;
                }
            }
        },
        {
            title: 'Default Max Amount',
            dataIndex: 'defaultMaxAmount',
            key: 'defaultMaxAmount',
            render: (amount: number) => amount?.toLocaleString() || 'N/A'
        },
        {
            title: 'Multiplier',
            dataIndex: 'defaultSavingsMultiplier',
            key: 'defaultSavingsMultiplier',
            render: (multiplier: number) => `${multiplier}x`
        },
        {
            title: 'High Balance Threshold',
            dataIndex: 'highBalanceThreshold',
            key: 'highBalanceThreshold',
            render: (amount: number) => amount?.toLocaleString() || 'N/A'
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            render: (active: boolean, record: LoanEligibilityConfigDTO) => (
                <Switch
                    checked={active}
                    onChange={() => handleStatusToggle(record.id!)}
                    loading={toggleActive.isPending}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: LoanEligibilityConfigDTO) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/admin/loan-settings/eligibility/${record.id}`)}
                        />
                    </Tooltip>
                    <Tooltip title="View Details">
                        <Button
                            type="link"
                            icon={<InfoCircleOutlined />}
                            onClick={() => showDetails(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record.id!, record.configName!)}
                            loading={deleteConfig.isPending}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    // Show details modal
    const showDetails = (config: LoanEligibilityConfigDTO) => {
        Modal.info({
            title: `Configuration: ${config.configName}`,
            width: 800,
            content: (
                <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label="Branch" span={1}>
                        {config.branch || 'All Branches'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Product (GL No)" span={1}>
                        {config.glNo || 'All Products'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Default Max Amount" span={1}>
                        {config.defaultMaxAmount?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Default Multiplier" span={1}>
                        {config.defaultSavingsMultiplier}x
                    </Descriptions.Item>
                    <Descriptions.Item label="High Balance Threshold" span={1}>
                        {config.highBalanceThreshold?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="High Balance Max Amount" span={1}>
                        {config.highBalanceMaxAmount?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="High Balance Multiplier" span={1}>
                        {config.highBalanceMultiplier}x
                    </Descriptions.Item>
                    <Descriptions.Item label="Min Savings Balance" span={1}>
                        {config.minimumSavingsBalance?.toLocaleString() || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Max Loan-to-Savings Ratio" span={1}>
                        {config.maxLoanToSavingsRatio
                            ? `${(config.maxLoanToSavingsRatio * 100).toFixed(2)}%`
                            : 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Min Savings History (Months)" span={1}>
                        {config.minSavingsHistoryMonths || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Consider Existing Loans" span={1}>
                        {config.considerExistingLoans ? 'Yes' : 'No'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Priority" span={1}>
                        {config.priority}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status" span={2}>
                        <Tag color={config.active ? 'green' : 'red'}>
                            {config.active ? 'Active' : 'Inactive'}
                        </Tag>
                    </Descriptions.Item>
                    {config.notes && (
                        <Descriptions.Item label="Notes" span={2}>
                            {config.notes}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Created By" span={1}>
                        {config.createdBy || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created Date" span={1}>
                        {config.createdDate
                            ? new Date(config.createdDate).toLocaleString()
                            : 'N/A'}
                    </Descriptions.Item>
                </Descriptions>
            )
        });
    };

    if (isLoading) {
        return (
            <Card>
                <Spin size="large" tip="Loading configurations..." />
            </Card>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Card
                style={{
                    backgroundColor: dark ? '#1f1f1f' : '#ffffff',
                    borderRadius: '8px'
                }}
            >
                <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            Loan Eligibility Configurations
                        </Title>
                        <Text type="secondary">
                            Manage loan eligibility calculation parameters
                        </Text>
                    </Col>
                    <Col>
                        <Space>
                            <Tooltip title="Evict Cache">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleEvictCache}
                                    loading={evictCache.isPending}
                                >
                                    Clear Cache
                                </Button>
                            </Tooltip>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => navigate('/admin/loan-settings/eligibility/new')}
                            >
                                New Configuration
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Input
                            placeholder="Search by name, branch, product, or notes..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                            size="large"
                        />
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} configurations`
                    }}
                    scroll={{ x: 1200 }}
                />
            </Card>
        </div>
    );
};

export default LoanEligibilityConfigListView;
